import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { SelectionGeometry } from "src/js/stores/map/store";
import { Nullable } from "src/js/types/Nullable";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import { Designation } from "react-migration/domains/constraints/typings/baseTypes/Designation";
import DESIGNATIONS_SNAPSHOT_BY_GEOMETRY from "./designationsSnapshotByGeometry.gql";
import LISTED_BUILDINGS_BY_GEOMETRY from "./listedBuildingsByGeometry.gql";

const DATA_STATUSES = ["live"];
const SOURCE_STATUSES = ["adopted"];

type DesignationSnapshot = Pick<Designation, "id" | "sub_category_id">;

export interface Snapshot {
  listedBuildings: {
    listed_buildings: DesignationSnapshot[];
  };
  protectedLand: {
    aonb: DesignationSnapshot[];
    greenbelt: DesignationSnapshot[];
    common_land: { id: string }[];
  };
  floodRisk: {
    very_low_flood_risk: DesignationSnapshot[];
    low_flood_risk: DesignationSnapshot[];
    medium_flood_risk: DesignationSnapshot[];
    high_flood_risk: DesignationSnapshot[];
  };
}

type ListedBuildingType = {
  isListedBuilding: DesignationSnapshot[];
  nearbyListedBuildings: DesignationSnapshot[];
};

export function useListedBuildingsByGeometry(geometry: Nullable<SelectionGeometry>) {
  const { data, loading, error } = useQuery<{ nearbyListedBuildings: DesignationSnapshot[] }>(
    LISTED_BUILDINGS_BY_GEOMETRY,
    {
      variables: {
        geometry,
        sourceStatuses: SOURCE_STATUSES,
        bufferMeters: 100,
        dataStatuses: DATA_STATUSES,
      },
      skip: !geometry,
      client: routedClient,
      context: {
        endpoint: LandTechEndpoints.Constraints,
      },
    }
  );

  const {
    data: radiusData,
    loading: radiusLoading,
    error: radiusError,
  } = useQuery<{ nearbyListedBuildings: DesignationSnapshot[] }>(LISTED_BUILDINGS_BY_GEOMETRY, {
    variables: {
      geometry,
      sourceStatuses: SOURCE_STATUSES,
      bufferMeters: 0,
      dataStatuses: DATA_STATUSES,
    },
    skip: !geometry,
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Constraints,
    },
  });

  const listedBuildings = useMemo<Nullable<ListedBuildingType>>(() => {
    if (loading || radiusLoading) return null;

    const { nearbyListedBuildings = [] } = data || {};
    const { nearbyListedBuildings: isListedBuilding = [] } = radiusData || {};

    return { isListedBuilding, nearbyListedBuildings };
  }, [data, loading, radiusData, radiusLoading]);

  return [listedBuildings, loading, error || radiusError] as [ListedBuildingType, boolean, Error];
}
